/**
 * WordCountPanel — collapsible word-count adjustment bar inside the chapter editor.
 *
 * Shows:
 *   • Current chapter word count vs chapter target (editable)
 *   • Colour-coded gap (amber if short, green if at/over target)
 *   • Total manuscript words vs manuscript total target
 *   • "Expand with Claude" button (gated: Indie Author tier+)
 *
 * Lives below the ManuscriptToolboxDrawer, above the ManuscriptBody.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ChevronsDown, ChevronDown, Loader2, Lock, LockOpen, Zap, Target, BookOpen, Settings2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { cn } from '@/lib/utils';
import { usePW } from '@/lib/store';
import { wordCountApi, chaptersApi, projectsApi } from '@/lib/api';
import { toast } from 'sonner';

// ── helpers ───────────────────────────────────────────────────────────────────

function countWords(text) {
  return text ? text.trim().split(/\s+/).filter(Boolean).length : 0;
}

function loadPinned() {
  try { return window.localStorage.getItem('pw_wc_panel_pinned') === '1'; } catch { return false; }
}

// ── Gap pill ─────────────────────────────────────────────────────────────────

function GapPill({ current, target }) {
  if (!target) return null;
  const gap = target - current;
  const pct = Math.min(100, Math.round((current / target) * 100));

  if (gap <= 0) {
    return (
      <span className="flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-semibold text-emerald-600"
            data-testid="wc-gap-pill-ok">
        <LockOpen className="h-2.5 w-2.5" />
        {pct}% · on target
      </span>
    );
  }
  const color = gap > 500 ? 'bg-rose-500/15 text-rose-600' : 'bg-amber-500/15 text-amber-600';
  return (
    <span className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${color}`}
          data-testid="wc-gap-pill-short">
      {gap.toLocaleString()} words short · {pct}%
    </span>
  );
}

// ── Progress bar ─────────────────────────────────────────────────────────────

function MiniBar({ pct, className }) {
  const fill = pct >= 100 ? 'bg-emerald-500' : pct >= 70 ? 'bg-amber-400' : 'bg-rose-400';
  return (
    <div className={cn('h-1 overflow-hidden rounded-full bg-muted', className)}>
      <div className={`h-full rounded-full transition-all duration-500 ${fill}`}
           style={{ width: `${Math.min(100, pct)}%` }} />
    </div>
  );
}

// ── Expand result panel ───────────────────────────────────────────────────────

function ExpandResultPane({ result, chapter, onAccept, onDiscard, accepting }) {
  const words = countWords(result.expanded_content);
  return (
    <div className="mt-2 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 space-y-2"
         data-testid="wc-expand-result">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[12.5px] font-semibold text-emerald-700">
          Expansion ready — {result.original_words.toLocaleString()} → {words.toLocaleString()} words
        </p>
        <div className="flex gap-1.5">
          <Button
            size="sm"
            className="h-6 gap-1 px-2.5 text-[12px] bg-emerald-600 hover:bg-emerald-700"
            onClick={onAccept}
            disabled={accepting}
            data-testid="wc-expand-accept-btn"
          >
            {accepting ? <Loader2 className="h-3 w-3 animate-spin" /> : <ChevronsDown className="h-3 w-3" />}
            Apply
          </Button>
          <Button
            size="sm" variant="ghost"
            className="h-6 px-2 text-[12px] text-muted-foreground"
            onClick={onDiscard}
            data-testid="wc-expand-discard-btn"
          >
            Discard
          </Button>
        </div>
      </div>
      <div className="max-h-28 overflow-y-auto rounded-md bg-background/60 px-2.5 py-2 text-[12px] text-muted-foreground leading-relaxed">
        {result.expanded_content.slice(0, 600)}{result.expanded_content.length > 600 ? '…' : ''}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function WordCountPanel({ canExpand = false }) {
  const { chapters, activeChapterId, project, setChapters, updateProject } = usePW();
  const chapter = chapters.find((c) => c.id === activeChapterId);

  const [open,        setOpen]       = useState(false);
  const [pinned,      setPinned]     = useState(loadPinned);
  const [chTarget,    setChTarget]   = useState('');
  const [saving,      setSaving]     = useState(false);
  const [expanding,   setExpanding]  = useState(false);
  const [accepting,   setAccepting]  = useState(false);
  const [expandRes,   setExpandRes]  = useState(null);
  // Project-default slider local state (avoids calling API on every tick)
  const [projDefault, setProjDefault] = useState(2500);
  const [savingProj,  setSavingProj]  = useState(false);

  const inputRef = useRef(null);

  const chWords   = countWords(chapter?.content || '');
  const chTargetN = parseInt(chTarget, 10) || chapter?.word_count_target || project?.words_per_chapter || 2500;
  const msWords   = chapters.reduce((a, c) => a + countWords(c.content || ''), 0);
  const msTarget  = project?.target_words || project?.targetWords || 50000;
  const msPct     = Math.min(100, Math.round((msWords / msTarget) * 100));

  // Sync target input from chapter metadata
  useEffect(() => {
    const t = chapter?.word_count_target || project?.words_per_chapter || 2500;
    setChTarget(String(t));
    setExpandRes(null);
  }, [chapter?.id, chapter?.word_count_target, project?.words_per_chapter]);

  // Sync project default slider
  useEffect(() => {
    setProjDefault(project?.words_per_chapter || 2500);
  }, [project?.words_per_chapter]);

  const isOpen = open || pinned;

  const togglePin = () => {
    setPinned((p) => {
      const next = !p;
      try { window.localStorage.setItem('pw_wc_panel_pinned', next ? '1' : '0'); } catch { /* noop */ }
      return next;
    });
  };

  // `saveTarget` accepts an explicit value so it is immune to the React
  // stale-closure problem that occurs when called from `onValueCommit`
  // (where `setChTarget` and the callback fire in the same synchronous tick).
  const saveTarget = useCallback(async (explicitValue) => {
    if (!chapter?.id) return;
    const t = explicitValue != null ? parseInt(explicitValue, 10) : parseInt(chTarget, 10);
    if (!t || t < 1) return;
    setSaving(true);
    try {
      await wordCountApi.setChapterTarget(chapter.id, t);
      toast.success(`Chapter target set to ${t.toLocaleString()} words`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not save target');
    } finally {
      setSaving(false);
    }
  }, [chapter?.id, chTarget]);

  const saveProjectDefault = useCallback(async (value) => {
    if (!project?.id) return;
    setSavingProj(true);
    try {
      await projectsApi.update(project.id, { words_per_chapter: value });
      if (updateProject) await updateProject({ words_per_chapter: value });
      toast.success(`Project default set to ${value.toLocaleString()} words/chapter`);
    } catch {
      toast.error('Could not save project default');
    } finally {
      setSavingProj(false);
    }
  }, [project?.id, updateProject]);

  const handleExpandClick = async () => {
    if (!chapter?.id || !project?.id) return;
    const t = parseInt(chTarget, 10) || chTargetN;
    if (t <= chWords) {
      toast.info('Chapter already meets or exceeds the target.');
      return;
    }
    setExpanding(true);
    setExpandRes(null);
    try {
      const res = await wordCountApi.expandChapter(project.id, chapter.id, t);
      setExpandRes(res);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 402) {
        toast.error(typeof detail === 'object' ? detail.message : (detail || 'Upgrade to Indie Author to use Claude Expand.'));
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Expand failed. Please try again.');
      }
    } finally {
      setExpanding(false);
    }
  };

  const handleAccept = async () => {
    if (!expandRes || !chapter?.id) return;
    setAccepting(true);
    try {
      await chaptersApi.update(chapter.id, { content: expandRes.expanded_content });
      setChapters((prev) =>
        prev.map((c) => c.id === chapter.id
          ? { ...c, content: expandRes.expanded_content, words: expandRes.expanded_words }
          : c,
        ),
      );
      setExpandRes(null);
      toast.success(`Chapter expanded to ${expandRes.expanded_words.toLocaleString()} words`);
    } catch {
      toast.error('Could not save expanded chapter');
    } finally {
      setAccepting(false);
    }
  };

  if (!chapter) return null;

  return (
    <div className="relative shrink-0 border-b border-panel-border bg-panel-header/10"
         data-testid="word-count-panel">

      {/* ── Handle ── */}
      <div className="flex h-6 items-center justify-between px-2">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-1.5 text-[12px] font-medium text-muted-foreground transition-colors hover:text-foreground"
          data-testid="wc-panel-toggle"
          title="Word count & expansion"
        >
          <Target className="h-3 w-3" />
          <span>
            {chWords.toLocaleString()} {chTargetN ? `/ ${chTargetN.toLocaleString()}` : ''} words
          </span>
          <GapPill current={chWords} target={chTargetN || null} />
          <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        <div className="flex items-center gap-1">
          {/* Manuscript total progress */}
          <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground/70"
                title={`Manuscript: ${msWords.toLocaleString()} / ${msTarget.toLocaleString()} words`}>
            <BookOpen className="h-2.5 w-2.5" />
            MS {msPct}%
          </span>
          <button
            type="button"
            onClick={togglePin}
            data-testid="wc-panel-pin"
            title={pinned ? 'Unpin' : 'Pin open'}
            className={cn(
              'ml-1 flex h-4 w-4 items-center justify-center rounded-sm transition-colors',
              pinned ? 'text-primary' : 'text-muted-foreground hover:text-primary',
            )}
          >
            <Target className="h-2.5 w-2.5" />
          </button>
        </div>
      </div>

      {/* ── Expanded panel ── */}
      {isOpen && (
        <div className="px-3 pb-3 pt-1 space-y-2" data-testid="wc-panel-body">

          {/* Chapter target row — slider replaces the plain number input */}
          <div className="space-y-1.5" data-testid="wc-chapter-target-row">
            <div className="flex items-center justify-between gap-2">
              <label className="text-[11.5px] text-muted-foreground">This chapter target</label>
              <div className="flex items-center gap-1">
                {saving && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
                <span className="w-16 text-right text-[12px] font-semibold tabular-nums" data-testid="wc-chapter-target-display">
                  {parseInt(chTarget, 10).toLocaleString() || '—'}
                </span>
              </div>
            </div>
            <Slider
              value={[parseInt(chTarget, 10) || 2500]}
              onValueChange={([v]) => setChTarget(String(v))}
              onValueCommit={([v]) => { setChTarget(String(v)); saveTarget(v); }}
              min={500}
              max={10000}
              step={100}
              className="w-full"
              data-testid="wc-chapter-target-slider"
            />
            <div className="flex justify-between text-[10px] text-muted-foreground/60 select-none">
              <span>500</span><span>2,500</span><span>5,000</span><span>10k</span>
            </div>
          </div>

          {/* Chapter progress bar */}
          <div className="flex items-center gap-2">
            <MiniBar pct={Math.round((chWords / (chTargetN || 1)) * 100)} className="flex-1" />
            <span className="shrink-0 text-[11.5px] text-muted-foreground tabular-nums">
              {chWords.toLocaleString()} / {(chTargetN || 0).toLocaleString()}
            </span>
          </div>

          {/* Expand button */}
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className={cn('h-6 gap-1 px-2.5 text-[12px]', !canExpand && 'opacity-60 cursor-not-allowed')}
              onClick={canExpand ? handleExpandClick : () => toast.info('Upgrade to Indie Author to use Claude Expand.')}
              disabled={expanding}
              data-testid="wc-expand-btn"
              title={canExpand ? 'Ask Claude to expand this chapter' : 'Upgrade to Indie Author to use Claude Expand'}
            >
              {expanding ? <Loader2 className="h-3 w-3 animate-spin" /> : canExpand ? <Zap className="h-3 w-3 text-amber-500" /> : <Lock className="h-3 w-3 text-muted-foreground/60" />}
              {expanding ? 'Expanding…' : 'Expand with Claude'}
            </Button>
          </div>

          {/* Project default words/chapter slider */}
          <div className="space-y-1.5 rounded-md border border-border/50 bg-muted/30 px-3 py-2" data-testid="wc-project-default-row">
            <div className="flex items-center justify-between gap-2">
              <label className="flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
                <Settings2 className="h-3 w-3" />
                Project default (new chapters)
              </label>
              <div className="flex items-center gap-1">
                {savingProj && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
                <span className="w-16 text-right text-[12px] font-semibold tabular-nums" data-testid="wc-project-default-display">
                  {projDefault.toLocaleString()}
                </span>
              </div>
            </div>
            <Slider
              value={[projDefault]}
              onValueChange={([v]) => setProjDefault(v)}
              onValueCommit={([v]) => saveProjectDefault(v)}
              min={1500}
              max={6000}
              step={100}
              className="w-full"
              data-testid="wc-project-default-slider"
            />
            <p className="text-[10px] text-muted-foreground/60">Drag to change. Existing chapters keep their own targets.</p>
          </div>

          {/* Manuscript total */}
          <div className="flex items-center gap-2">
            <span className="text-[11.5px] text-muted-foreground">Manuscript total:</span>
            <MiniBar pct={msPct} className="w-32" />
            <span className="text-[11.5px] text-muted-foreground">
              {msWords.toLocaleString()} / {msTarget.toLocaleString()} words ({msPct}%)
            </span>
          </div>

          {/* Expansion result */}
          {expandRes && (
            <ExpandResultPane
              result={expandRes}
              chapter={chapter}
              onAccept={handleAccept}
              onDiscard={() => setExpandRes(null)}
              accepting={accepting}
            />
          )}
        </div>
      )}
    </div>
  );
}
